<template>
  <main class="demarche-page" :style="{ '--demarche-header-height': headerHeight + 'px' }">
    <div class="demarche-return"><router-link :to="returnDestination">← {{ returnDestination === '/mes-menus' ? 'Retour à mon espace' : 'Retour à l’accueil' }}</router-link></div>
<nav class="demarche-toc" aria-label="Sommaire"><strong>Comment fonctionne Mes Meilleurs Menus ?</strong><div><a href="#demarche-a"><b>A</b> Notre ambition</a><a href="#demarche-b"><b>B</b> Notre démarche en un coup d’œil</a><a href="#demarche-c"><b>C</b> La science comme point de départ</a><a href="#demarche-d"><b>D</b> De la science à l’assiette</a><a href="#demarche-e"><b>E</b> Une démarche pensée pour la vraie vie</a><a href="#demarche-f"><b>F</b> Devenir autonome</a></div></nav>
    <section id="demarche-a" class="section ambition">
      <div class="ambition-intro">
        <p class="eyebrow"><span class="step-number">A</span> NOTRE AMBITION</p>
        <h1>
          Une Alimentation Positive et Durable
          <span>accessible à tous</span>
        </h1>
        <h2>Mieux manger doit pouvoir s’intégrer dans la vraie vie.</h2>
      </div>
      <div class="ambition-body">
        <figure class="ambition-visual">
          <img
            src="@/assets/notre-demarche/alimentation-positive-durable-sante-plaisir-climat-budget.png"
            alt="Une alimentation positive et durable qui concilie santé, plaisir, climat, budget et vie quotidienne"
          />
        </figure>

        <div class="ambition-copy">
          <p>
            Bien manger joue un rôle essentiel pour notre santé. Nos choix
            alimentaires ont également un impact sur le climat. Pourtant,
            améliorer son alimentation ne devrait pas signifier suivre un
            régime compliqué, renoncer au plaisir de manger ou consacrer un
            budget toujours plus important à ses courses.
          </p>

          <p>
            C’est de cette conviction qu’est née la démarche de
            <strong>Mes Meilleurs Menus : rendre possible, au quotidien, une
            Alimentation Positive et Durable.</strong>
          </p>

          <p>
            Par <strong>Alimentation Positive et Durable</strong>, nous
            entendons une alimentation qui cherche à concilier plusieurs
            dimensions qui ne devraient pas être opposées :
            <strong>prendre soin de sa santé, garder le plaisir de manger,
            respecter davantage le climat, maîtriser son budget et pouvoir
            appliquer tout cela dans la vraie vie.</strong>
          </p>

          <p>
            Notre objectif n’est donc pas de rechercher une alimentation
            parfaite, ni d’imposer de nouvelles contraintes. Il est de donner
            à chacun des solutions concrètes et des repères simples pour
            <strong>améliorer progressivement ses habitudes alimentaires.</strong>
          </p>
        </div>
      </div>

      <div class="engagement-cards">
        <article class="engagement-card engagement-free">
          <div class="engagement-topline">
            <span class="engagement-icon" aria-hidden="true">
              <svg viewBox="0 0 48 48"><circle cx="24" cy="15" r="7"/><circle cx="11" cy="19" r="5"/><circle cx="37" cy="19" r="5"/><path d="M12 39c0-8 5-13 12-13s12 5 12 13"/><path d="M2 38c0-6 3-10 9-11M46 38c0-6-3-10-9-11"/></svg>
            </span>
            <span class="engagement-number">Engagement 1</span>
          </div>
          <h3>Rester accessible à tous</h3>
          <p>
            Mes Meilleurs Menus est géré par une association et vise à aider
            gratuitement le plus grand nombre à améliorer ses habitudes
            alimentaires. Les menus, recettes et outils proposés sont
            accessibles gratuitement, parce que mieux manger ne devrait pas
            être réservé à ceux qui peuvent payer un abonnement.
          </p>
        </article>

        <article class="engagement-card engagement-budget">
          <div class="engagement-topline">
            <span class="engagement-icon" aria-hidden="true">
              <svg viewBox="0 0 48 48"><ellipse cx="24" cy="11" rx="14" ry="6"/><path d="M10 11v8c0 3 6 6 14 6s14-3 14-6v-8"/><path d="M10 19v8c0 3 6 6 14 6s14-3 14-6v-8"/><path d="M10 27v8c0 3 6 6 14 6s14-3 14-6v-8"/></svg>
            </span>
            <span class="engagement-number">Engagement 2</span>
          </div>
          <h3>Des menus abordables</h3>
          <p>
            Mieux manger ne signifie pas nécessairement acheter davantage ou
            choisir des produits coûteux. Mes Meilleurs Menus privilégie une
            alimentation simple, davantage de produits bruts et végétaux et
            cherche à concilier <strong>qualité nutritionnelle, plaisir et
            maîtrise du budget.</strong>
          </p>
        </article>
      </div>

      <div class="transition-card">
        <p>
          Mais comment transformer cette ambition en quelque chose de concret
          dans l’assiette ?
        </p>
        <span>C’est toute la démarche de Mes Meilleurs Menus.</span>
      </div>
    </section>

    <section id="demarche-b" class="approach-overview">
      <div class="overview-inner">
        <div class="overview-heading">
          <p class="eyebrow"><span class="step-number">B</span> NOTRE DÉMARCHE EN UN COUP D’ŒIL</p>
          <h2>De la science à l’autonomie</h2>
          <p>
            Notre démarche part des connaissances scientifiques pour les transformer
            en solutions simples et concrètes dans l’assiette. Le but n’est pas de
            multiplier les règles, mais de mieux comprendre son alimentation pour
            pouvoir, progressivement, faire ses propres choix.
          </p>
        </div>

        <div class="overview-flow" aria-label="De la science et la physiologie vers l'autonomie alimentaire">
          <article class="flow-card flow-science">
            <span class="flow-kicker">LE POINT DE DÉPART</span>
            <h3>Science &amp;<br>physiologie</h3>
            <p>Comprendre les besoins de l’organisme et les effets de l’alimentation.</p>
          </article>

          <span class="flow-arrow" aria-hidden="true">↓</span>

          <article class="flow-card flow-apd">
            <span class="flow-kicker">NOTRE VISION</span>
            <h3>Alimentation<br>Positive et Durable</h3>
            <div class="flow-pillars">
              <span>Santé</span><span>Plaisir</span><span>Climat</span><span>Accessibilité</span>
            </div>
          </article>

          <span class="flow-arrow" aria-hidden="true">↓</span>

          <article class="flow-card flow-tools">
            <span class="flow-kicker">NOS OUTILS</span>
            <div class="tool-pair">
              <div>
                <strong>MES MEILLEURS MENUS</strong>
                <small>Construire votre alimentation</small>
              </div>
              <span>+</span>
              <div>
                <strong>MEALIM</strong>
                <small>Comprendre votre repas</small>
              </div>
            </div>
          </article>

          <span class="flow-arrow" aria-hidden="true">↓</span>

          <article class="flow-card flow-plate">
            <span class="flow-kicker">DANS LA VRAIE VIE</span>
            <h3>Des solutions concrètes<br>dans l’assiette</h3>
          </article>

          <span class="flow-arrow" aria-hidden="true">↓</span>

          <div class="flow-autonomy">
            <strong>Comprendre</strong><span>→</span>
            <strong>Apprendre</strong><span>→</span>
            <strong>Devenir autonome</strong>
          </div>
        </div>
      </div>
    </section>

    <section id="demarche-c" class="science-foundations">
      <div class="science-inner">
        <header class="science-heading">
          <p class="eyebrow"><span class="step-number">C</span> LA SCIENCE COMME POINT DE DÉPART</p>
          <h2>Comprendre avant de choisir</h2>
          <p class="science-lead">
            Mes Meilleurs Menus s’appuie sur les connaissances scientifiques en nutrition,
            physiologie et métabolisme pour transformer des données parfois complexes en
            choix alimentaires simples. L’objectif n’est pas de transformer chaque repas
            en équation, mais de donner des repères solides pour mieux manger au quotidien.
          </p>
        </header>

        <div class="science-principles">
          <details class="science-principle principle-mediterranean">
            <summary>
              <span class="science-icon" aria-hidden="true">🥗</span>
              <span class="principle-summary">
                <span class="commitment-badge">ENGAGEMENT 3 — MODÈLE MÉDITERRANÉEN</span>
                <span class="principle-label">MODÈLE ALIMENTAIRE</span>
                <strong class="principle-title">S’inspirer du modèle méditerranéen</strong>
                <span class="principle-intro">Donner une place importante aux légumes, fruits, légumineuses, céréales complètes, fruits à coque, huile d’olive et produits de la mer, avec une place plus limitée pour les viandes rouges et transformées.</span>
                <span class="principle-open">Comprendre pourquoi <b aria-hidden="true">↓</b></span>
              </span>
            </summary>

            <div class="principle-detail mediterranean-detail">
              <div class="article-bridge">
                <span>POUR ALLER PLUS LOIN</span>
                <p>
                  Cette approche est développée par <strong>Joffrey Zoll et Anouk Charlot</strong> dans l’article
                  <a href="https://theconversation.com/lalimentation-positive-cest-sinspirer-du-regime-mediterraneen-et-limiter-le-sucre-222821" target="_blank" rel="noopener noreferrer">« L’alimentation positive, c’est s’inspirer du régime méditerranéen et limiter le sucre »</a>,
                  publié dans <em>The Conversation</em>.
                </p>
              </div>

              <h4>Pourquoi le modèle méditerranéen ?</h4>
              <p>
                Le « régime méditerranéen » est avant tout un <strong>modèle alimentaire global</strong>, historiquement
                construit autour des ressources et habitudes du bassin méditerranéen. Il ne repose donc ni sur un
                aliment miracle, ni sur l’exclusion d’une famille d’aliments. Son socle est largement végétal :
                légumes et fruits, légumineuses, céréales peu raffinées, fruits à coque et huile d’olive. Les poissons
                et produits de la mer y occupent une place régulière, tandis que les viandes rouges, les charcuteries
                et les produits très sucrés sont consommés plus occasionnellement.
              </p>
              <p>
                Cette logique rejoint directement la philosophie de Mes Meilleurs Menus : <strong>ce qui compte est
                l’organisation de l’alimentation dans son ensemble</strong>. La qualité d’un repas dépend des aliments
                qui le composent, de leurs associations et des quantités ; la qualité de l’alimentation se construit
                ensuite à l’échelle de la journée et de la semaine.
              </p>
              <p>
                Notre article consacré à l’« alimentation positive » insistait déjà sur ce point : le modèle
                méditerranéen apporte simultanément des aliments riches en fibres, acides gras insaturés, vitamines,
                minéraux et composés bioactifs comme les polyphénols. Il limite également naturellement plusieurs
                caractéristiques du modèle occidental — excès de produits très transformés, de viandes transformées
                et de produits riches en sucres ajoutés — lorsqu’il est appliqué dans son esprit traditionnel.
              </p>

              <div class="literature-section">
                <div class="literature-heading">
                  <span>CE QUE MONTRE LA LITTÉRATURE SCIENTIFIQUE</span>
                  <h4>Un faisceau de données, des cohortes aux essais randomisés</h4>
                  <p>
                    L’intérêt du modèle méditerranéen ne repose pas sur une seule étude. Il est documenté par plusieurs
                    décennies de recherche : études de cohortes, essais d’intervention, méta-analyses et revues
                    systématiques. La force des preuves n’est cependant pas identique pour tous les critères de santé.
                  </p>
                </div>

                <div class="evidence-topic">
                  <h5>1. Maladies cardiovasculaires : les données les plus solides</h5>
                  <p>
                    L’un des jalons historiques est le <strong>Lyon Diet Heart Study</strong>, conduit en France chez
                    des patients ayant déjà présenté un infarctus. Cet essai de prévention secondaire a observé une
                    diminution importante des récidives cardiovasculaires avec une alimentation de type méditerranéen.
                    Il a contribué, dès les années 1990, à replacer la qualité globale de l’alimentation au cœur de la
                    prévention cardiovasculaire.
                  </p>
                  <p>
                    L’essai espagnol <strong>PREDIMED</strong> a ensuite étudié 7 447 personnes de 55 à 80 ans à haut
                    risque cardiovasculaire mais sans maladie cardiovasculaire à l’inclusion. Après un suivi médian
                    de 4,8 ans, l’incidence du critère combinant infarctus, AVC ou décès cardiovasculaire était plus
                    faible dans les groupes « alimentation méditerranéenne + huile d’olive extra-vierge » et
                    « alimentation méditerranéenne + fruits à coque » que dans le groupe conseil de réduction des
                    graisses. La republication de 2018 a toutefois été nécessaire après l’identification d’écarts au
                    protocole de randomisation ; les analyses corrigées ont conservé une association favorable.
                  </p>
                  <p>
                    Les synthèses plus récentes vont globalement dans la même direction, tout en rappelant que la
                    qualité méthodologique des essais et revues est hétérogène. Le signal cardiovasculaire est donc
                    particulièrement convaincant, mais il doit être présenté avec les limites propres aux essais
                    nutritionnels.
                  </p>
                </div>

                <div class="evidence-topic">
                  <h5>2. Mortalité globale : une association retrouvée dans de grandes cohortes</h5>
                  <p>
                    Une méta-analyse dose-réponse regroupant <strong>29 études prospectives, environ 1,68 million de
                    participants et plus de 220 000 décès</strong> a retrouvé une association inverse entre adhésion
                    au modèle méditerranéen et mortalité toutes causes. Comme il s’agit principalement de données
                    observationnelles, elles ne permettent pas à elles seules de prouver une causalité, mais leur
                    ampleur et leur cohérence renforcent le faisceau d’arguments.
                  </p>
                  <p>
                    Une revue « parapluie » de méta-analyses portant sur plus de 12 millions de participants concluait
                    également à des données robustes pour plusieurs grands critères, notamment mortalité globale,
                    maladies cardiovasculaires, maladie coronarienne, infarctus et diabète, tout en soulignant une
                    hétérogénéité importante pour certains autres résultats.
                  </p>
                </div>

                <div class="evidence-topic">
                  <h5>3. Métabolisme et diabète de type 2</h5>
                  <p>
                    Le modèle méditerranéen est également associé à un profil métabolique plus favorable. Une
                    méta-analyse de 57 essais contrôlés, représentant près de 37 000 participants, a rapporté des
                    améliorations de nombreux paramètres : poids et tour de taille, pression artérielle, glycémie,
                    insulinémie, insulinorésistance, triglycérides et plusieurs marqueurs lipidiques ou inflammatoires.
                  </p>
                  <p>
                    Concernant la prévention du diabète de type 2, une méta-analyse actualisée publiée en 2025,
                    regroupant 24 cohortes prospectives et près d’un million de participants, conclut avec un niveau
                    de certitude modéré qu’une meilleure adhésion au modèle méditerranéen est associée à un risque
                    plus faible de développer un diabète de type 2. Là encore, une association de cohorte n’équivaut
                    pas à une démonstration causale individuelle.
                  </p>
                </div>

                <div class="evidence-topic">
                  <h5>4. Cancer : un signal intéressant, mais à interpréter selon les localisations</h5>
                  <p>
                    Les données disponibles ne permettent pas de parler d’un effet identique sur « le cancer » pris
                    comme une seule maladie. Une méta-analyse de 117 études et plus de 3,2 millions de participants a
                    néanmoins associé une forte adhésion au modèle méditerranéen à une mortalité par cancer plus faible
                    et à un risque plus faible pour plusieurs localisations, notamment colorectal, sein, voies
                    aérodigestives supérieures, estomac et foie. La majorité de ces données restent observationnelles.
                  </p>
                  <p>
                    Une synthèse plus récente intégrée aux recommandations italiennes sur le régime méditerranéen,
                    portant sur plus de 8 millions de participants, retrouve des réductions modestes pour plusieurs
                    cancers et évalue la certitude globale des principaux résultats comme modérée. Cette prudence est
                    importante : <strong>un modèle alimentaire contribue à la prévention, il ne constitue évidemment
                    pas un traitement anticancéreux.</strong>
                  </p>
                </div>

                <div class="evidence-topic">
                  <h5>5. Cerveau et cognition : des résultats encourageants, encore moins définitifs</h5>
                  <p>
                    Plusieurs cohortes associent une meilleure adhésion au modèle méditerranéen à un risque plus faible
                    de déclin cognitif, de trouble cognitif léger ou de maladie d’Alzheimer. Une méta-analyse publiée
                    en 2022 a ainsi retrouvé des associations favorables dans les cohortes et de petits bénéfices sur
                    certains domaines de mémoire dans les essais randomisés. Mais les essais restent moins nombreux,
                    les définitions du modèle méditerranéen varient et tous les domaines cognitifs ne répondent pas de
                    la même manière. Il s’agit donc d’un champ prometteur, mais moins établi que la prévention
                    cardiovasculaire.
                  </p>
                </div>

                <div class="evidence-topic">
                  <h5>6. Pourquoi ces effets sont-ils physiologiquement plausibles ?</h5>
                  <p>
                    Il serait réducteur d’attribuer les bénéfices à un seul nutriment. Le modèle méditerranéen combine
                    plusieurs caractéristiques susceptibles d’agir ensemble : densité élevée en aliments végétaux et
                    en fibres, acides gras mono- et polyinsaturés, polyphénols, moindre place des viandes transformées
                    et de nombreux aliments ultratransformés, ainsi qu’une meilleure qualité globale des glucides.
                  </p>
                  <p>
                    Les essais d’intervention montrent des effets sur plusieurs intermédiaires physiologiques :
                    pression artérielle, métabolisme glucido-lipidique, fonction vasculaire et certains marqueurs
                    inflammatoires. Une méta-analyse récente de 33 essais randomisés rapporte notamment des diminutions
                    de la hs-CRP, de l’IL-6 et de l’IL-17, sans amélioration significative de tous les marqueurs étudiés.
                    Cela illustre bien la logique que nous retenons : <strong>un effet systémique construit par un
                    ensemble d’aliments et de mécanismes, plutôt qu’une molécule miracle.</strong>
                  </p>
                </div>

                <div class="evidence-topic evidence-perspective">
                  <h5>7. Ce qu’il faut retenir… et ce qu’il ne faut pas faire dire aux études</h5>
                  <p>
                    La convergence des résultats explique pourquoi le modèle méditerranéen constitue l’un des socles
                    de Mes Meilleurs Menus. Mais la nutrition est un domaine où les études observationnelles sont
                    sensibles aux facteurs de confusion et où les essais à long terme sont difficiles à conduire en
                    double aveugle. Les populations, les scores d’adhésion et même la définition du « régime
                    méditerranéen » diffèrent entre études.
                  </p>
                  <p>
                    Nous ne présentons donc pas ce modèle comme une garantie individuelle contre une maladie. Nous le
                    considérons comme <strong>un cadre alimentaire particulièrement bien étayé</strong>, que MMM adapte
                    à la vie quotidienne et complète par d’autres dimensions : qualité des glucides, besoins
                    nutritionnels, plaisir, accessibilité et impact climatique.
                  </p>
                </div>
              </div>

              <div class="plate-box">
                <span>CE QUE CELA CHANGE DANS VOTRE ASSIETTE</span>
                <p>
                  Concrètement : davantage de légumes et de fruits, de légumineuses et de céréales peu raffinées ;
                  des fruits à coque ; l’huile d’olive et d’autres huiles végétales adaptées ; du poisson et des
                  produits de la mer régulièrement ; et une place plus mesurée pour les viandes rouges, charcuteries
                  et produits très sucrés. L’objectif n’est pas de « manger méditerranéen » au sens géographique,
                  mais <strong>d’en transposer les grands principes avec les aliments disponibles localement et au fil
                  des saisons.</strong>
                </p>
              </div>

              <div class="detail-references">
                <h4>Références scientifiques</h4>
                <ol>
                  <li><strong>GBD 2017 Diet Collaborators.</strong> Health effects of dietary risks in 195 countries, 1990–2017. <em>Lancet.</em> 2019;393:1958–1972. <a href="https://pubmed.ncbi.nlm.nih.gov/30954305/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Willett WC et al.</strong> Mediterranean diet pyramid: a cultural model for healthy eating. <em>Am J Clin Nutr.</em> 1995;61:1402S–1406S. <a href="https://pubmed.ncbi.nlm.nih.gov/7754995/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Bach-Faig A et al.</strong> Mediterranean diet pyramid today. Science and cultural updates. <em>Public Health Nutr.</em> 2011;14:2274–2284. <a href="https://pubmed.ncbi.nlm.nih.gov/22166184/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>de Lorgeril M et al.</strong> Mediterranean diet, traditional risk factors, and the rate of cardiovascular complications after myocardial infarction: final report of the Lyon Diet Heart Study. <em>Circulation.</em> 1999;99:779–785. <a href="https://pubmed.ncbi.nlm.nih.gov/9989963/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Estruch R et al.</strong> Primary Prevention of Cardiovascular Disease with a Mediterranean Diet Supplemented with Extra-Virgin Olive Oil or Nuts. <em>N Engl J Med.</em> 2018;378:e34. <a href="https://pubmed.ncbi.nlm.nih.gov/29897866/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Dinu M et al.</strong> Mediterranean diet and multiple health outcomes: an umbrella review of meta-analyses of observational studies and randomised trials. <em>Eur J Clin Nutr.</em> 2018;72:30–43. <a href="https://pubmed.ncbi.nlm.nih.gov/28488692/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Soltani S et al.</strong> Adherence to the Mediterranean Diet in Relation to All-Cause Mortality: a systematic review and dose-response meta-analysis. <em>Adv Nutr.</em> 2019;10:1029–1039. <a href="https://pubmed.ncbi.nlm.nih.gov/31111871/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Papadaki A et al.</strong> The Effect of the Mediterranean Diet on Metabolic Health: a systematic review and meta-analysis of controlled trials in adults. <em>Nutrients.</em> 2020;12:3342. <a href="https://pubmed.ncbi.nlm.nih.gov/33143083/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Zeraattalab-Motlagh S et al.</strong> Mediterranean dietary pattern and the risk of type 2 diabetes: a systematic review and dose-response meta-analysis. <em>Eur J Nutr.</em> 2022;61. <a href="https://pubmed.ncbi.nlm.nih.gov/35001218/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Schwingshackl L et al.</strong> An updated systematic review and meta-analysis on adherence to Mediterranean diet and risk of cancer. <em>Eur J Nutr.</em> 2021;60:1561–1586. <a href="https://pubmed.ncbi.nlm.nih.gov/32770356/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Fu J et al.</strong> Association between the Mediterranean diet and cognitive health among healthy adults: a systematic review and meta-analysis. <em>Front Nutr.</em> 2022;9:946361. <a href="https://pubmed.ncbi.nlm.nih.gov/35967772/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Keshani M et al.</strong> Mediterranean Diet Reduces Inflammation in Adults: a systematic review and meta-analysis of randomized controlled trials. <em>Nutr Rev.</em> 2026;84:1776–1790. <a href="https://pubmed.ncbi.nlm.nih.gov/41211687/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Reidlinger DP et al.</strong> The effectiveness of the Mediterranean Diet for primary and secondary prevention of cardiovascular disease: an umbrella review. <em>Nutr Diet.</em> 2024. <a href="https://pubmed.ncbi.nlm.nih.gov/39143663/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Zoll J, Charlot A.</strong> L’alimentation positive, c’est s’inspirer du régime méditerranéen et limiter le sucre. <em>The Conversation.</em> 2024. <a href="https://theconversation.com/lalimentation-positive-cest-sinspirer-du-regime-mediterraneen-et-limiter-le-sucre-222821" target="_blank" rel="noopener noreferrer">Lire l’article ↗</a></li>
                </ol>
              </div>
            </div>
          </details>

          <details class="science-principle">
            <summary>
              <span class="science-icon" aria-hidden="true">🌾</span>
              <span class="principle-summary">
                <span class="principle-label">QUALITÉ DES GLUCIDES</span>
                <strong class="principle-title">Regarder au-delà de la quantité</strong>
                <span class="principle-intro">Fibres, céréales complètes, matrice alimentaire et degré de transformation comptent autant que la quantité totale de glucides.</span>
                <span class="principle-open"><span class="open-label">Approfondir ce principe</span><span class="close-label">Refermer les explications</span> <b aria-hidden="true">↓</b></span>
              </span>
            </summary>
            <div class="principle-detail">
              <div class="article-bridge">
                <span>UNE IDÉE CENTRALE DE NOTRE ALIMENTATION POSITIVE</span>
                <p>
                  Dans notre article <a href="https://theconversation.com/lalimentation-positive-cest-sinspirer-du-regime-mediterraneen-et-limiter-le-sucre-222821" target="_blank" rel="noopener noreferrer">publié dans <em>The Conversation</em></a>,
                  nous insistions déjà sur l’intérêt de limiter les sucres non essentiels et de privilégier des sources glucidiques de meilleure qualité.
                </p>
              </div>
              <h4>Tous les glucides ne se valent pas</h4>
              <p>
                Deux aliments apportant une quantité proche de glucides peuvent avoir des effets physiologiques très différents.
                Leur teneur en fibres, leur structure, leur degré de raffinage et la matrice dans laquelle se trouvent les glucides
                modifient notamment la digestion, la satiété et la réponse métabolique. MMM privilégie donc une lecture
                <strong>qualitative</strong> des glucides plutôt qu’une logique d’exclusion.
              </p>
              <div class="evidence-box">
                <span>CE QUE MONTRE LA LITTÉRATURE</span>
                <p>
                  La vaste synthèse de Reynolds et collaborateurs publiée dans <em>The Lancet</em> a regroupé essais et cohortes
                  sur plusieurs marqueurs de qualité glucidique. Les associations les plus cohérentes concernent les
                  <strong>fibres alimentaires et les céréales complètes</strong>, avec des risques plus faibles pour plusieurs
                  maladies non transmissibles et pour la mortalité. Les données sur l’indice glycémique existent également,
                  mais sont globalement moins robustes que celles concernant les fibres.
                </p>
                <p>
                  Une méta-analyse de 45 études prospectives retrouve également une relation dose-réponse entre consommation
                  de céréales complètes et diminution du risque cardiovasculaire et de mortalité. Ces études observationnelles
                  ne démontrent pas à elles seules la causalité, mais leur cohérence avec les essais sur les facteurs de risque
                  renforce l’intérêt de privilégier des aliments glucidiques peu raffinés et riches en fibres.
                </p>
              </div>
              <div class="plate-box">
                <span>CE QUE CELA CHANGE DANS VOTRE ASSIETTE</span>
                <p>
                  L’objectif n’est pas simplement de « manger moins de glucides ». Privilégiez plus souvent les légumes secs,
                  céréales complètes ou semi-complètes, pains riches en fibres, fruits entiers et aliments peu raffinés,
                  tout en limitant la place des produits riches en sucres ajoutés.
                </p>
              </div>
              <div class="detail-references">
                <h4>Références scientifiques</h4>
                <ol>
                  <li><strong>Reynolds A et al.</strong> Carbohydrate quality and human health: a series of systematic reviews and meta-analyses. <em>Lancet.</em> 2019;393:434–445. <a href="https://pubmed.ncbi.nlm.nih.gov/30638909/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Aune D et al.</strong> Whole grain consumption and risk of cardiovascular disease, cancer, and all cause and cause specific mortality. <em>BMJ.</em> 2016;353:i2716. <a href="https://pubmed.ncbi.nlm.nih.gov/27301975/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Yao F et al.</strong> Dietary intake of total and different sources of fibre and risk of all-cause, cardiovascular, and cancer mortality. <em>Front Nutr.</em> 2023;10:1153165. <a href="https://pubmed.ncbi.nlm.nih.gov/37854351/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Anses.</strong> Actualisation des repères du PNNS : références nutritionnelles en macronutriments et fibres, 2016–2017. <a href="https://www.anses.fr/fr/system/files/NUT2012SA0103Ra-1.pdf" target="_blank" rel="noopener noreferrer">Rapport Anses ↗</a></li>
                </ol>
              </div>
            </div>
          </details>

          <details class="science-principle">
            <summary>
              <span class="science-icon" aria-hidden="true">📉</span>
              <span class="principle-summary">
                <span class="commitment-badge">APPROFONDIR LA QUALITÉ GLYCÉMIQUE</span>
                <span class="principle-label">RÉPONSE GLYCÉMIQUE</span>
                <strong class="principle-title">Limiter les réponses glycémiques trop importantes</strong>
                <span class="principle-intro">L’indice glycémique est utile, mais la réponse physiologique dépend de la portion, du repas et aussi de la personne.</span>
                <span class="principle-open"><span class="open-label">Approfondir ce principe</span><span class="close-label">Refermer les explications</span> <b aria-hidden="true">↓</b></span>
              </span>
            </summary>
            <div class="principle-detail">
              <h4>De l’indice glycémique à la réponse réelle au repas</h4>
              <p>
                L’indice glycémique décrit la réponse à une quantité standardisée de glucides d’un aliment consommé dans
                des conditions définies. Dans la vraie vie, nous mangeons des repas : la quantité consommée, les fibres,
                les lipides, les protéines, la préparation culinaire et l’association des aliments influencent la réponse
                glycémique. La <strong>charge glycémique</strong> ajoute la notion de quantité de glucides effectivement consommée.
              </p>
              <p>
                Il existe aussi une variabilité interindividuelle. Dans une étude de 800 personnes suivies en continu,
                Zeevi et collaborateurs ont observé des réponses glycémiques parfois très différentes à des repas identiques.
                Cette observation ne rend pas les recommandations générales inutiles ; elle rappelle qu’un chiffre attaché
                à un aliment ne suffit pas à prédire parfaitement la réponse d’une personne.
              </p>
              <div class="evidence-box">
                <span>CE QUE MONTRE LA LITTÉRATURE</span>
                <p>
                  Chez les personnes diabétiques, la méta-analyse de Chiavaroli et collaborateurs portant sur des essais
                  randomisés retrouve avec les régimes à IG ou charge glycémique plus bas de petites améliorations de
                  l’HbA1c et de plusieurs facteurs de risque cardiométabolique. Ce résultat est intéressant mais concerne
                  une population diabétique : il ne doit pas être transposé tel quel à toute la population.
                </p>
                <p>
                  À l’échelle de la population générale, les données les plus solides sur la qualité glucidique restent
                  celles concernant les fibres et les céréales complètes. MMM utilise donc la réponse glycémique comme
                  <strong>une dimension du repas parmi d’autres</strong>, et non comme un critère unique.
                </p>
              </div>
              <div class="plate-box">
                <span>CE QUE CELA CHANGE DANS VOTRE ASSIETTE</span>
                <p>
                  Plutôt que d’interdire un aliment sur la seule base de son IG, regardez le repas complet : portion,
                  accompagnement végétal, fibres, protéines, matières grasses et degré de transformation. C’est cette
                  logique de repas que MMM cherche à rendre concrète.
                </p>
              </div>
              <div class="detail-references">
                <h4>Références scientifiques</h4>
                <ol>
                  <li><strong>Chiavaroli L et al.</strong> Effect of low glycaemic index or load dietary patterns on glycaemic control and cardiometabolic risk factors in diabetes. <em>BMJ.</em> 2021;374:n1651. <a href="https://pubmed.ncbi.nlm.nih.gov/34348965/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Zeevi D et al.</strong> Personalized Nutrition by Prediction of Glycemic Responses. <em>Cell.</em> 2015;163:1079–1094. <a href="https://pubmed.ncbi.nlm.nih.gov/26590418/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Reynolds A et al.</strong> Carbohydrate quality and human health. <em>Lancet.</em> 2019;393:434–445. <a href="https://pubmed.ncbi.nlm.nih.gov/30638909/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Anses.</strong> Actualisation des repères du PNNS : glucides et fibres. <a href="https://www.anses.fr/system/files/NUT2012SA0103Ra-2.pdf" target="_blank" rel="noopener noreferrer">Rapport Anses ↗</a></li>
                </ol>
              </div>
            </div>
          </details>

          <details class="science-principle">
            <summary>
              <span class="science-icon" aria-hidden="true">⚖️</span>
              <span class="principle-summary">
                <span class="commitment-badge">ENGAGEMENT 4</span>
                <span class="principle-label">BESOINS NUTRITIONNELS</span>
                <strong class="principle-title">Couvrir les besoins de l’organisme</strong>
                <span class="principle-intro">Énergie, protéines, lipides, glucides, fibres, vitamines et minéraux : la qualité d’une alimentation ne se résume pas aux calories.</span>
                <span class="principle-open"><span class="open-label">Approfondir ce principe</span><span class="close-label">Refermer les explications</span> <b aria-hidden="true">↓</b></span>
              </span>
            </summary>
            <div class="principle-detail">
              <h4>Apporter de l’énergie, mais aussi les nutriments dont l’organisme a besoin</h4>
              <p>
                Manger suffisamment ne signifie pas nécessairement couvrir l’ensemble des besoins nutritionnels.
                L’organisme utilise les <strong>protéines, glucides et lipides</strong> comme sources d’énergie,
                matériaux de construction ou substrats de nombreuses voies métaboliques. Certains acides aminés et
                certains acides gras sont indispensables et doivent être apportés par l’alimentation.
              </p>
              <p>
                À ces macronutriments s’ajoutent les <strong>vitamines et les minéraux</strong>. Ils n’apportent pas
                d’énergie, mais interviennent dans de très nombreuses fonctions : métabolisme énergétique, fonctionnement
                neuromusculaire, transport de l’oxygène, immunité, maintien du tissu osseux ou encore protection des
                cellules. Les besoins varient selon le nutriment, l’âge, le sexe et certaines situations physiologiques.
                L’Anses établit pour cela des références nutritionnelles spécifiques pour la population française.
              </p>

              <div class="evidence-box">
                <span>DES BESOINS QUI NE SE RÉSUMENT PAS À UN SEUL CHIFFRE</span>
                <p>
                  Les références nutritionnelles françaises distinguent plusieurs types de repères selon les nutriments.
                  Pour les adultes, l’Anses retient notamment des intervalles de référence pour la contribution des
                  protéines, lipides et glucides à l’apport énergétique, ainsi que des références spécifiques pour les
                  acides gras essentiels, les vitamines et les minéraux.
                </p>
                <p>
                  Pour les fibres, l’Anses retient chez l’adulte un <strong>apport satisfaisant de 30 g par jour</strong>.
                  L’enjeu n’est toutefois pas de transformer chaque repas en tableau de chiffres : les références servent
                  surtout à construire une alimentation suffisamment variée et complémentaire dans le temps.
                </p>
              </div>

              <div class="nutrient-box">
                <span>LES 24 NUTRIMENTS SUIVIS PAR MES MEILLEURS MENUS</span>
                <p>
                  Pour rendre cette approche concrète, Mes Meilleurs Menus suit dans les menus hebdomadaires
                  <strong>24 nutriments</strong>, choisis pour couvrir un ensemble large de vitamines, minéraux et fibres
                  à partir des données de composition des aliments et des références nutritionnelles.
                </p>
                <div class="nutrient-groups">
                  <div>
                    <strong>Fibres</strong>
                    <small>Fibres alimentaires</small>
                  </div>
                  <div>
                    <strong>11 minéraux et oligoéléments</strong>
                    <small>Calcium · Fer · Magnésium · Phosphore · Potassium · Zinc · Cuivre · Manganèse · Iode · Sélénium · Sodium</small>
                  </div>
                  <div>
                    <strong>12 vitamines</strong>
                    <small>Vitamine A · B1 · B2 · B3 · B5 · B6 · B9 · B12 · C · D · E · K1</small>
                  </div>
                </div>
                <p>
                  Ce suivi complète celui de l’<strong>énergie et des macronutriments — protéines, lipides et glucides</strong>.
                  L’objectif n’est pas que chaque repas couvre isolément toutes les références, mais que les différents
                  aliments et repas se complètent pour construire une alimentation cohérente dans le temps.
                </p>
              </div>

              <h4>Les fibres : bien au-delà du transit intestinal</h4>
              <p>
                Les fibres alimentaires constituent un cas particulier : elles ne sont pas digérées comme les autres
                glucides dans l’intestin grêle et certaines peuvent être utilisées par les microorganismes du côlon.
                Elles participent à la régulation du transit, mais leur intérêt ne s’arrête pas là. Les synthèses de la
                littérature associent des apports plus élevés en fibres et en céréales complètes à un risque plus faible
                de plusieurs maladies non transmissibles et de mortalité.
              </p>
              <p>
                Toutes les fibres n’ont cependant pas les mêmes propriétés. Leur solubilité, leur viscosité et leur
                fermentescibilité diffèrent selon les aliments. C’est une raison supplémentaire pour privilégier
                <strong>la diversité des sources</strong> : légumes, fruits, légumineuses, céréales complètes, fruits à
                coque et graines plutôt que de raisonner uniquement en grammes de fibres.
              </p>

              <div class="evidence-box microbiota-box">
                <span>FIBRES &amp; MICROBIOTE INTESTINAL</span>
                <p>
                  Le microbiote intestinal est un écosystème complexe dont la composition et l’activité sont notamment
                  influencées par l’alimentation. Une partie des fibres fermentescibles sert de substrat à certaines
                  bactéries intestinales, qui produisent notamment des <strong>acides gras à chaîne courte</strong>
                  comme l’acétate, le propionate ou le butyrate.
                </p>
                <p>
                  Les essais d’intervention montrent que certaines fibres peuvent modifier l’abondance de groupes
                  bactériens et la production de métabolites, mais les réponses dépendent fortement du type de fibre et
                  des individus. Une méta-analyse de 64 essais chez des adultes en bonne santé a notamment retrouvé une
                  augmentation des <em>Bifidobacterium</em> et, plus modestement, des <em>Lactobacillus</em>, sans
                  augmentation globale de la diversité microbienne.
                </p>
                <p>
                  C’est pourquoi nous préférons parler de <strong>nourrir un écosystème microbien diversifié par une
                  alimentation elle-même diversifiée</strong>, plutôt que de promettre qu’un aliment isolé permettrait
                  à lui seul d’obtenir un « bon microbiote ».
                </p>
              </div>

              <div class="plate-box">
                <span>CE QUE CELA CHANGE DANS VOTRE ASSIETTE</span>
                <p>
                  Mes Meilleurs Menus ne cherche donc pas seulement à apporter la bonne quantité d’énergie. Le générateur
                  associe les aliments et ajuste les portions en tenant compte des <strong>macronutriments, des fibres,
                  des vitamines et des minéraux</strong>, tout en recherchant la diversité des sources alimentaires au
                  cours des repas, de la journée et de la semaine.
                </p>
                <p>
                  En pratique, cela conduit à donner régulièrement une place aux légumes et fruits, légumineuses,
                  céréales complètes, fruits à coque et graines, mais aussi aux autres familles d’aliments utiles à la
                  couverture des besoins. <strong>La complémentarité compte davantage que la recherche d’un aliment
                  parfait.</strong>
                </p>
              </div>

              <div class="detail-references">
                <h4>Références scientifiques et institutionnelles</h4>
                <ol>
                  <li><strong>Anses.</strong> Actualisation des repères du PNNS : élaboration des références nutritionnelles en macronutriments et fibres, 2016–2017. <a href="https://www.anses.fr/sites/default/files/NUT2012SA0103Ra-1.pdf" target="_blank" rel="noopener noreferrer">Rapport Anses ↗</a></li>
                  <li><strong>Anses.</strong> Les références nutritionnelles : actualisation des références en vitamines et minéraux pour la population française. <a href="https://anses.fr/fr/system/files/NUT2018SA0238Ra.pdf" target="_blank" rel="noopener noreferrer">Rapport Anses ↗</a></li>
                  <li><strong>Reynolds A, Mann J, Cummings J, et al.</strong> Carbohydrate quality and human health: a series of systematic reviews and meta-analyses. <em>Lancet.</em> 2019;393:434–445. <a href="https://pubmed.ncbi.nlm.nih.gov/30638909/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>So D, Whelan K, Rossi M, et al.</strong> Dietary fiber intervention on gut microbiota composition in healthy adults: a systematic review and meta-analysis. <em>Am J Clin Nutr.</em> 2018;107:965–983. <a href="https://pubmed.ncbi.nlm.nih.gov/29757343/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Vinelli V, Biscotti P, Martini D, et al.</strong> Effects of dietary fibers on short-chain fatty acids and gut microbiota composition in healthy adults: a systematic review. <em>Nutrients.</em> 2022;14:2559. <a href="https://pubmed.ncbi.nlm.nih.gov/35807739/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Anses – Ciqual.</strong> Table de composition nutritionnelle des aliments. <a href="https://ciqual.anses.fr/" target="_blank" rel="noopener noreferrer">Ciqual ↗</a></li>
                </ol>
              </div>
            </div>
          </details>

          <details class="science-principle">
            <summary>
              <span class="science-icon" aria-hidden="true">🌱</span>
              <span class="principle-summary">
                <span class="commitment-badge">ENGAGEMENT 5</span>
                <span class="principle-label">ALIMENTATION &amp; CLIMAT</span>
                <strong class="principle-title">Donner davantage de place au végétal</strong>
                <span class="principle-intro">Rééquilibrer l’assiette vers davantage d’aliments végétaux permet d’agir à la fois sur sa composition et sur son impact environnemental.</span>
                <span class="principle-open"><span class="open-label">Approfondir ce principe</span><span class="close-label">Refermer les explications</span> <b aria-hidden="true">↓</b></span>
              </span>
            </summary>
            <div class="principle-detail">
              <h4>Une alimentation positive doit aussi être durable</h4>
              <p>
                L’alimentation mobilise des terres, de l’eau et de l’énergie et contribue aux émissions de gaz à effet
                de serre. Tous les aliments n’ont cependant pas le même impact et, pour un même aliment, les modes de
                production peuvent entraîner de fortes variations. Il faut donc éviter les classements trop simplistes.
              </p>
              <p>
                Un résultat ressort néanmoins de manière très régulière des analyses de cycle de vie : à l’échelle
                globale, les produits issus des ruminants ont en moyenne une empreinte climatique et foncière nettement
                plus élevée que la plupart des aliments végétaux. Rééquilibrer l’assiette vers davantage de végétal
                constitue donc un levier important, sans imposer pour autant une alimentation entièrement végétarienne.
              </p>
              <div class="evidence-box">
                <span>CE QUE MONTRE LA LITTÉRATURE</span>
                <p>
                  Malgré les différences entre systèmes de production, la conclusion générale est claire :
                  <strong>la composition de l’alimentation est un levier important pour réduire son impact climatique.</strong>
                  Les analyses de cycle de vie de Poore et Nemecek montrent notamment que les aliments issus des ruminants,
                  en particulier la viande bovine et ovine, ont en moyenne des émissions de gaz à effet de serre et une
                  utilisation des terres nettement supérieures à celles de la plupart des aliments végétaux.
                </p>
                <p>
                  Les travaux de Springmann et les revues systématiques consacrées aux régimes durables convergent vers
                  la même conclusion : <strong>une alimentation donnant davantage de place aux végétaux et réduisant la
                  part des produits animaux les plus impactants permet généralement de diminuer fortement son empreinte
                  environnementale.</strong> Tous les indicateurs ne varient pas nécessairement dans le même sens — l’eau,
                  les terres ou la biodiversité doivent aussi être considérées — mais cette nuance ne remet pas en cause
                  le principal levier identifié : rééquilibrer l’assiette vers davantage de végétal.
                </p>
              </div>
              <div class="evidence-box">
                <span>ET POUR LA VIANDE ?</span>
                <p>
                  Sur le plan sanitaire, les données sont particulièrement cohérentes pour recommander de
                  <strong>limiter les viandes transformées</strong>. Pour la viande rouge non transformée, les résultats
                  sont plus nuancés et ne justifient pas de la présenter comme un aliment à supprimer systématiquement.
                  Sur le plan climatique, en revanche, la viande de ruminants — notamment bovine et ovine — fait partie
                  des aliments dont l’impact moyen est le plus élevé.
                </p>
              </div>

              <div class="decision-box">
                <span>LE CHOIX DE MES MEILLEURS MENUS</span>
                <p>
                  Nous avons donc fait un choix clair : <strong>ne pas supprimer la viande, mais en réduire la place dans
                  la semaine</strong>, limiter particulièrement les viandes transformées et donner davantage de place aux
                  légumineuses et aux autres aliments végétaux. Pour la viande rouge, notre logique est celle de la
                  <strong>modération</strong>, avec une attention particulière aux viandes de ruminants lorsque l’on
                  considère l’impact climatique.
                </p>
                <p>
                  Autrement dit, MMM ne cherche ni à imposer une alimentation végétarienne, ni à considérer toutes les
                  viandes comme équivalentes. Notre décision est de construire des menus <strong>plus végétaux, moins
                  dépendants de la viande et compatibles avec le plaisir de manger</strong>. C’est ce compromis entre
                  santé, climat et vie quotidienne que nous retenons pour notre Alimentation Positive et Durable.
                </p>
              </div>
              <div class="plate-box">
                <span>CE QUE CELA CHANGE DANS VOTRE ASSIETTE</span>
                <p>
                  Davantage de légumes, légumineuses, céréales et autres sources végétales ; une place raisonnée pour les
                  aliments d’origine animale ; moins de viandes transformées ; et une attention à la saisonnalité.
                  L’objectif est de faire évoluer progressivement l’équilibre de l’assiette sans opposer santé, plaisir et climat.
                </p>
              </div>
              <div class="detail-references">
                <h4>Références scientifiques</h4>
                <ol>
                  <li><strong>Poore J, Nemecek T.</strong> Reducing food’s environmental impacts through producers and consumers. <em>Science.</em> 2018;360:987–992. <a href="https://pubmed.ncbi.nlm.nih.gov/29853680/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Springmann M et al.</strong> Options for keeping the food system within environmental limits. <em>Nature.</em> 2018;562:519–525. <a href="https://pubmed.ncbi.nlm.nih.gov/30305731/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Jarmul S et al.</strong> Climate change mitigation through dietary change: a systematic review of environmental footprints and health effects of sustainable diets. <em>Environ Res Lett.</em> 2020. <a href="https://pubmed.ncbi.nlm.nih.gov/33897807/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Aleksandrowicz L et al.</strong> The Impacts of Dietary Change on Greenhouse Gas Emissions, Land Use, Water Use, and Health: A Systematic Review. <em>PLoS One.</em> 2016;11:e0165797. <a href="https://pubmed.ncbi.nlm.nih.gov/27812156/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                  <li><strong>Zeraatkar D et al.</strong> Red and Processed Meat Consumption and Risk for All-Cause Mortality and Cardiometabolic Outcomes. <em>Ann Intern Med.</em> 2019;171:703–710. <a href="https://pubmed.ncbi.nlm.nih.gov/31569213/" target="_blank" rel="noopener noreferrer">PubMed ↗</a></li>
                </ol>
              </div>
            </div>
          </details>
        </div>

        <div class="science-conclusion">
          <span aria-hidden="true">→</span>
          <p><strong>La science nous donne le cap.</strong> Mes Meilleurs Menus la transforme en solutions concrètes dans l’assiette.</p>
        </div>

        <details class="science-references">
          <summary>Voir les références scientifiques</summary>
          <ol>
            <li><strong>Estruch R. et al.</strong> Primary Prevention of Cardiovascular Disease with a Mediterranean Diet Supplemented with Extra-Virgin Olive Oil or Nuts. <em>N Engl J Med.</em> 2018;378:e34. doi:10.1056/NEJMoa1800389.</li>
            <li><strong>Reynolds A. et al.</strong> Carbohydrate quality and human health: a series of systematic reviews and meta-analyses. <em>Lancet.</em> 2019;393:434–445. doi:10.1016/S0140-6736(18)31809-9.</li>
            <li><strong>Chiavaroli L. et al.</strong> Effect of low glycaemic index or load dietary patterns on glycaemic control and cardiometabolic risk factors in diabetes. <em>BMJ.</em> 2021;374:n1651. doi:10.1136/bmj.n1651.</li>
            <li><strong>Anses.</strong> Actualisation des références nutritionnelles en vitamines et minéraux. Rapport d’expertise collective, 2021.</li>
            <li><strong>Poore J., Nemecek T.</strong> Reducing food’s environmental impacts through producers and consumers. <em>Science.</em> 2018;360:987–992. doi:10.1126/science.aaq0216.</li>
          </ol>
        </details>
      </div>
    </section>

    <section id="demarche-d" class="science-to-plate">
      <div class="plate-tools-inner">
        <header class="plate-tools-heading">
          <p class="eyebrow"><span class="step-number">D</span> DE LA SCIENCE À L’ASSIETTE</p>
          <h2>Comment nos principes deviennent des menus</h2>
          <p>
            Les connaissances scientifiques donnent une direction. Mais elles ne fournissent pas directement un menu.
            Le travail de Mes Meilleurs Menus consiste à <strong>transformer les connaissances scientifiques présentées
            plus haut en principes utilisables dans la vraie vie</strong>, puis à faire coexister plusieurs objectifs
            qui ne sont pas toujours parfaitement alignés.
          </p>
          <p>
            Car bien manger ne consiste pas à optimiser un aliment ou un repas isolément. Il faut tenir compte de ce
            qui est mangé, de la manière dont les aliments sont associés au cours d’un repas, de la complémentarité
            entre les repas d’une journée et, finalement, de l’équilibre de l’alimentation dans le temps.
          </p>
          <p>
            C’est dans cette logique que s’inscrit le <strong>Score MEALIM</strong>. MEALIM signifie
            <strong>« Mesure de l’Équilibre, des Liens et des Interactions entre les Aliments et le Métabolisme »</strong> :
            un nom qui traduit notre volonté de ne pas considérer les aliments isolément, mais de mieux comprendre
            leur place et leurs associations à l’échelle du repas et de la journée.
          </p>
        </header>

        <div class="translation-flow">
          <article class="translation-card">
            <span class="translation-number">01</span>
            <span class="translation-kicker">TRADUIRE</span>
            <h3>Passer des connaissances à des critères concrets</h3>
            <p>
              Modèle méditerranéen, qualité des glucides, réponse glycémique, besoins nutritionnels, place du végétal,
              saisonnalité ou impact climatique : ces connaissances doivent devenir des
              <strong>principes réellement utilisables pour construire les repas et les menus</strong>.
            </p>
            <p>
              L’objectif n’est pas de reproduire mécaniquement une recommandation scientifique, mais d’en conserver
              le sens lorsqu’elle arrive dans l’assiette.
            </p>
          </article>

          <span class="translation-arrow" aria-hidden="true">→</span>

          <article class="translation-card">
            <span class="translation-number">02</span>
            <span class="translation-kicker">CROISER</span>
            <h3>Faire travailler plusieurs dimensions ensemble</h3>
            <p>
              Un repas ou un menu ne peut pas être considéré comme satisfaisant simplement parce qu’il répond
              parfaitement à un seul critère.
            </p>
            <p>
              Il faut simultanément tenir compte des <strong>besoins nutritionnels, des portions, de la qualité des
              aliments, de leur association au sein du repas, du mode alimentaire, de la diversité, des préférences
              et des principes de notre Alimentation Positive et Durable</strong>.
            </p>
          </article>

          <span class="translation-arrow" aria-hidden="true">→</span>

          <article class="translation-card">
            <span class="translation-number">03</span>
            <span class="translation-kicker">ARBITRER</span>
            <h3>Chercher la cohérence plutôt que la perfection</h3>
            <p>
              Dans la vraie vie, tous les objectifs ne peuvent pas être maximisés en permanence.
              <strong>Construire une bonne alimentation est donc aussi un exercice d’arbitrage.</strong>
            </p>
            <p>
              Chaque repas n’a pas besoin d’être parfait pris isolément : les repas peuvent se compléter au cours
              de la journée, comme les journées peuvent elles-mêmes s’inscrire dans l’équilibre d’une semaine.
            </p>
          </article>
        </div>

        <div class="scale-panel">
          <div class="scale-heading">
            <span>UNE DIFFÉRENCE ESSENTIELLE</span>
            <h3>Aliment, repas, journée, semaine : changer d’échelle pour mieux comprendre</h3>
            <p>
              Beaucoup de messages nutritionnels conduisent à classer les aliments les uns après les autres.
              Pourtant, <strong>la qualité d’une alimentation ne peut pas se résumer à la somme de « bons » et de
              « mauvais » aliments</strong>. Un aliment est consommé au sein d’un repas, ce repas s’inscrit dans une
              journée, et cette journée fait elle-même partie d’une alimentation qui se construit dans le temps.
            </p>
          </div>

          <div class="logic-visual" aria-label="Notre logique : de l’aliment au repas, à la journée puis à la semaine">
            <div class="logic-title">NOTRE LOGIQUE</div>
            <div class="logic-path" aria-hidden="true">
              <strong>ALIMENT</strong><span>→</span><strong>REPAS</strong><span>→</span><strong>JOURNÉE</strong><span>→</span><strong>SEMAINE</strong>
            </div>

            <div class="logic-stages">
              <article class="logic-stage logic-food">
                <div class="logic-stage-head"><span class="scale-dot">1</span><strong>ALIMENT</strong></div>
                <p class="logic-subtitle">La première brique de l’alimentation</p>
                <div class="logic-foods" aria-hidden="true"><span>🍅</span><span>🥬</span><span>🫘</span><span>🌾</span></div>
                <small>Sa composition nutritionnelle, sa structure et son degré de transformation en définissent les principales caractéristiques.</small>
              </article>

              <span class="logic-arrow" aria-hidden="true">→</span>

              <article class="logic-stage logic-meal">
                <div class="logic-stage-head"><span class="scale-dot">2</span><strong>REPAS</strong></div>
                <p class="logic-subtitle">Plusieurs aliments associés</p>
                <div class="logic-plate" aria-hidden="true"><span>🥗</span><span>🐟</span><span>🌾</span></div>
                <small>Leurs quantités, leurs caractéristiques et leurs associations déterminent ensemble la qualité physiologique du repas.</small>
              </article>

              <span class="logic-arrow" aria-hidden="true">→</span>

              <article class="logic-stage logic-day">
                <div class="logic-stage-head"><span class="scale-dot">3</span><strong>JOURNÉE</strong></div>
                <p class="logic-subtitle">Des repas qui se complètent</p>
                <div class="logic-day-meals" aria-hidden="true">
                  <span><b>Matin</b>🥣</span><span><b>Midi</b>🍽️</span><span><b>Soir</b>🥗</span>
                </div>
                <small>Les différents repas peuvent se compléter au cours de la journée et construire une cohérence alimentaire sur 24 heures.</small>
              </article>

              <span class="logic-arrow" aria-hidden="true">→</span>

              <article class="logic-stage logic-week">
                <div class="logic-stage-head"><span class="scale-dot">4</span><strong>SEMAINE</strong></div>
                <p class="logic-subtitle">Une alimentation qui s’organise dans le temps</p>
                <div class="logic-calendar" aria-hidden="true">
                  <span>L</span><span>M</span><span>M</span><span>J</span><span>V</span><span>S</span><span>D</span>
                </div>
                <small>La semaine fait apparaître la diversité, la fréquence et la complémentarité des choix alimentaires dans le temps.</small>
              </article>
            </div>

            <div class="logic-brackets">
              <div class="logic-bracket mealim-bracket">
                <span class="bracket-line"></span>
                <div><strong>Score MEALIM</strong><small>Comprendre et évaluer la qualité physiologique du repas et de la journée</small></div>
              </div>
              <div class="logic-bracket mmm-bracket">
                <span class="bracket-line"></span>
                <div><strong>MES MEILLEURS MENUS</strong><small>Prendre en compte l’ensemble des niveaux pour construire et organiser l’alimentation dans le temps</small></div>
              </div>
            </div>
          </div>
        </div>

        <div class="method-tools">
          <article class="method-tool">
            <span class="method-label">MES MEILLEURS MENUS</span>
            <h3>Construire et organiser l’alimentation dans le temps</h3>
            <p>
              Mes Meilleurs Menus prend en compte <strong>l’ensemble des échelles, de l’aliment à la semaine</strong>.
              L’enjeu n’est pas simplement de sélectionner de « bonnes recettes », mais de construire une cohérence
              alimentaire en tenant compte des besoins, des portions, de la diversité, des préférences, du mode
              alimentaire et des principes de l’Alimentation Positive et Durable.
            </p>
          </article>

          <article class="method-tool method-tool-mealim">
            <span class="method-label">SCORE MEALIM</span>
            <h3>Comprendre le repas et prendre du recul sur la journée</h3>
            <p>
              Le <strong>Score MEALIM</strong> porte une lecture complémentaire. À l’échelle du repas, il aide à
              comprendre sa qualité physiologique et ce qui pourrait être amélioré. À l’échelle de la journée, il
              permet de considérer les différents repas ensemble et leur complémentarité.
            </p>
            <p>
              Son objectif est avant tout pédagogique : aider à comprendre progressivement ce qui fait la qualité
              d’un repas et comment les repas s’articulent au cours d’une journée.
            </p>
          </article>
        </div>

        <div class="method-conclusion">
          <span>NOTRE LOGIQUE</span>
          <p>
            <strong>La science ne fournit pas un menu tout fait.</strong> Notre travail consiste à traduire les
            connaissances, croiser plusieurs dimensions, changer d’échelle puis arbitrer pour proposer des solutions
            applicables au quotidien.
          </p>
          <p>
            Le <strong>Score MEALIM</strong> permet de comprendre et d’évaluer l’alimentation à l’échelle du repas et
            de la journée. <strong>Mes Meilleurs Menus</strong> prend en compte l’ensemble des niveaux pour construire
            et organiser l’alimentation dans le temps. Ces deux approches poursuivent finalement le même objectif :
            <strong>ne pas seulement dire quoi manger, mais permettre progressivement de comprendre pourquoi et de
            devenir plus autonome dans ses choix alimentaires.</strong>
          </p>
        </div>
      </div>
    </section>

    <section id="demarche-e" class="real-life">
      <div class="real-life-inner">
        <header class="real-life-heading">
          <p class="eyebrow"><span class="step-number">E</span> UNE DÉMARCHE PENSÉE POUR LA VRAIE VIE</p>
          <h2>Des principes qui s’adaptent aux personnes, aux produits et au quotidien</h2>
          <p>
            Transformer la science en menus ne suffit pas. Pour être réellement utile, notre démarche doit aussi
            tenir compte des différences entre les personnes, de la qualité des aliments choisis, du plaisir de
            manger, des saisons et des contraintes du quotidien.
          </p>
          <p>
            Ces engagements prolongent les principes scientifiques présentés plus haut et expliquent
            <strong>comment Mes Meilleurs Menus cherche à les rendre applicables au plus grand nombre.</strong>
          </p>
        </header>

        <div class="commitment-grid">
          <article class="commitment-card">
            <span class="science-icon commitment-icon" aria-hidden="true">👤</span>
            <span class="commitment-kicker">ENGAGEMENT 6 · PERSONNALISER</span>
            <h3>Des menus adaptés aux besoins</h3>
            <p>
              Les besoins alimentaires ne sont pas identiques pour tout le monde. Mes Meilleurs Menus tient notamment
              compte de <strong>l’âge, du sexe, de la taille, du poids et du niveau d’activité physique</strong> afin
              d’adapter les propositions et les portions au profil de chacun.
            </p>
            <p>
              La personnalisation permet ainsi de passer de recommandations générales à une alimentation plus proche
              des besoins réels de la personne.
            </p>
          </article>

          <article class="commitment-card">
            <span class="science-icon commitment-icon" aria-hidden="true">🥦</span>
            <span class="commitment-kicker">ENGAGEMENT 7 · SÉLECTIONNER</span>
            <h3>Des aliments et des produits choisis avec exigence</h3>
            <p>
              La qualité d’un menu dépend aussi des aliments qui le composent. Nous sélectionnons les produits selon
              plusieurs dimensions : <strong>qualité nutritionnelle, intérêt dans le repas, goût, impact environnemental
              et accessibilité</strong>.
            </p>
            <p>
              L’objectif n’est pas de rechercher un aliment « parfait », mais de disposer de bonnes briques pour
              construire des repas cohérents avec notre Alimentation Positive et Durable.
            </p>
          </article>

          <article class="commitment-card">
            <span class="science-icon commitment-icon" aria-hidden="true">🍽️</span>
            <span class="commitment-kicker">ENGAGEMENT 8 · PLAISIR &amp; SAISONNALITÉ</span>
            <h3>Le plaisir, les recettes et les saisons</h3>
            <p>
              Mieux manger doit rester synonyme de plaisir. Les recettes s’inspirent notamment de la cuisine
              méditerranéenne tout en laissant une vraie place aux <strong>produits de saison, à la diversité des
              goûts et à des plats simples à préparer</strong>.
            </p>
            <p>
              La saisonnalité n’est donc pas seulement un principe affiché : elle participe à la manière dont les
              menus et les recettes évoluent au fil de l’année.
            </p>
          </article>

          <article class="commitment-card commitment-community">
            <span class="science-icon commitment-icon" aria-hidden="true">🤝</span>
            <span class="commitment-kicker">ENGAGEMENT 9 · ASSOCIATION</span>
            <h3>Une aventure ouverte à toutes et tous</h3>
            <p>
              Mes Meilleurs Menus est porté par une association avec une ambition simple : rendre une alimentation
              meilleure pour la santé et le climat accessible au plus grand nombre.
            </p>
            <p>
              Celles et ceux qui souhaitent aller plus loin peuvent aussi <strong>rejoindre l’association des
              Meilleurs Mangeurs</strong>, participer à l’aventure et apporter leur pierre à l’édifice. Faire évoluer
              les habitudes alimentaires est une démarche individuelle, mais c’est aussi un projet que nous pouvons
              construire collectivement.
            </p>
          </article>
        </div>


      </div>
    </section>

    <section id="demarche-f" class="autonomy-section">
      <div class="autonomy-inner">
        <header class="autonomy-heading">
          <p class="eyebrow"><span class="step-number">F</span> NOTRE VÉRITABLE OBJECTIF</p>
          <h2>Donner les clés pour devenir autonome</h2>
          <p>
            Mes Meilleurs Menus n’a pas vocation à décider indéfiniment à la place de chacun. Les menus, les recettes
            et le Score MEALIM sont aussi des <strong>outils d’apprentissage</strong> : ils doivent permettre de
            comprendre progressivement ce qui fait la qualité d’une alimentation et de développer ses propres repères.
          </p>
        </header>

        <div class="learning-path" aria-label="Observer, comprendre, modifier, apprendre puis devenir autonome">
          <article><span>1</span><strong>OBSERVER</strong><small>Ce qui compose les repas et les habitudes alimentaires.</small></article>
          <b aria-hidden="true">→</b>
          <article><span>2</span><strong>COMPRENDRE</strong><small>Pourquoi certains choix ou associations sont plus favorables.</small></article>
          <b aria-hidden="true">→</b>
          <article><span>3</span><strong>MODIFIER</strong><small>Faire évoluer progressivement un repas, une journée ou une habitude.</small></article>
          <b aria-hidden="true">→</b>
          <article><span>4</span><strong>APPRENDRE</strong><small>Transformer les explications et l’expérience en repères durables.</small></article>
          <b aria-hidden="true">→</b>
          <article class="learning-goal"><span>5</span><strong>DEVENIR AUTONOME</strong><small>Faire ses propres choix avec davantage de compréhension et de liberté.</small></article>
        </div>

        <div class="autonomy-tools">
          <p><strong>Mes Meilleurs Menus propose et accompagne.</strong> Il donne un cadre concret pour organiser l’alimentation.</p>
          <p><strong>Le Score MEALIM aide à comprendre.</strong> Il donne des repères pour lire la qualité physiologique du repas et de la journée.</p>
          <p><strong>L’expérience permet d’apprendre.</strong> À mesure que les repères sont compris, ils peuvent être réutilisés sans dépendre en permanence des outils.</p>
        </div>

        <div class="autonomy-final">
          <span>LE BUT N’EST PAS DE SUIVRE. LE BUT EST D’APPRENDRE.</span>
          <p>
            Commencer avec des menus, comprendre progressivement ses repas, acquérir de nouveaux réflexes puis être
            capable de faire ses propres choix : <strong>c’est cette autonomie alimentaire que Mes Meilleurs Menus
            cherche finalement à transmettre.</strong>
          </p>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { userState } from "@/utils/store";
export default {
  name: "NotreDemarche",
  data() { return { headerHeight: 153 }; },
  computed: {
    returnDestination() {
      if (userState.isLoggedIn()) return "/mes-menus";
      try { return localStorage.getItem("accessToken") || localStorage.getItem("refreshToken") ? "/mes-menus" : "/"; }
      catch (error) { return "/"; }
    }
  },
  mounted() {
    this.updateHeaderHeight();
    window.addEventListener("resize", this.updateHeaderHeight);
    const header = document.querySelector(".fixed-top");
    if (header && typeof ResizeObserver !== "undefined") {
      this.headerObserver = new ResizeObserver(this.updateHeaderHeight);
      this.headerObserver.observe(header);
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateHeaderHeight);
    if (this.headerObserver) this.headerObserver.disconnect();
  },
  methods: {
    updateHeaderHeight() {
      const header = document.querySelector(".fixed-top");
      this.headerHeight = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
    }
  }
};
</script>

<style scoped>
.demarche-page {
  font-family: "TangoSans", sans-serif;
  color: #26352f;
  background: #fffdf8;
}

.section {
  max-width: 1400px;
  margin: auto;
  padding: 70px max(5vw, 32px) 88px;
}

.ambition {
  margin-top: 153px;
}

.eyebrow {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 0 20px;
  color: #3f7f4f;
  font-size: clamp(1.05rem, 1.35vw, 1.25rem);
  font-weight: 900;
  letter-spacing: .055em;
  line-height: 1.2;
}

.step-number {
  display: inline-grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 50%;
  background: #76a957;
  color: #fff;
  font-size: 20px;
  letter-spacing: 0;
}

.ambition-intro {
  max-width: 830px;
  margin-bottom: 30px;
}

.ambition-intro h1 {
  margin: 0 0 16px;
  color: #004c40;
  font-size: clamp(48px, 5.2vw, 76px);
  font-weight: 800;
  line-height: .98;
  letter-spacing: -.025em;
}

.ambition-intro h1 span {
  display: block;
  color: #78aa58;
}

.ambition-intro h2 {
  margin: 0;
  max-width: 680px;
  color: #26352f;
  font-family: "TangoSans", sans-serif;
  font-size: clamp(23px, 2.1vw, 31px);
  font-weight: 700;
  line-height: 1.15;
}

.ambition-body::after {
  content: "";
  display: block;
  clear: both;
}

.ambition-visual {
  float: right;
  width: min(58%, 760px);
  margin: -135px 0 30px 48px;
}

.ambition-visual img {
  display: block;
  width: 100%;
  height: auto;
}

.ambition-copy {
  font-size: 18px;
  line-height: 1.55;
}

.ambition-copy p {
  margin: 0 0 17px;
}

.ambition-copy strong {
  color: #004c40;
  font-weight: 800;
}

.engagements-heading {
  clear: both;
  max-width: 920px;
  margin: 62px 0 26px;
}

.engagements-heading .eyebrow {
  margin-bottom: 8px;
}

.engagements-heading h2 {
  margin: 0;
  color: #004c40;
  font-family: "TangoSans", sans-serif;
  font-size: clamp(28px, 2.6vw, 38px);
  font-weight: 800;
  line-height: 1.12;
}

.engagement-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 26px;
}

.engagement-card {
  position: relative;
  overflow: hidden;
  min-height: 0;
  padding: 26px 30px 28px;
  border: 1px solid rgba(0, 76, 64, .07);
  border-radius: 22px;
  box-shadow: 0 10px 30px rgba(0, 76, 64, .06);
}

.engagement-free {
  background: linear-gradient(135deg, #f3f8ed 0%, #fbfcf8 100%);
}

.engagement-budget {
  background: linear-gradient(135deg, #fff8e6 0%, #fffdf6 100%);
}

.engagement-topline {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.engagement-icon {
  display: grid;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  place-items: center;
  border-radius: 50%;
  background: #fff;
  color: #5e9636;
  box-shadow: 0 5px 15px rgba(0, 76, 64, .08);
}

.engagement-budget .engagement-icon {
  color: #c99200;
}

.engagement-icon svg {
  width: 29px;
  height: 29px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.engagements-intro {
  max-width: 850px;
  margin: 12px 0 0;
  color: #52645d;
  font-size: 16px;
  line-height: 1.6;
}

.engagement-number {
  display: inline-block;
  padding: 7px 13px;
  border-radius: 999px;
  background: #e3efd2;
  color: #004c40;
  font-size: 14px;
  font-weight: 800;
}

.engagement-budget .engagement-number {
  background: #f7e7a5;
}

.engagement-card h3 {
  margin: 0 0 10px;
  color: #004c40;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.15;
}

.engagement-card p {
  margin: 0;
  color: #33443e;
  font-size: 16.5px;
  line-height: 1.55;
}

.transition-card {
  max-width: 1080px;
  margin: 44px auto 0;
  padding: 24px 34px;
  border-radius: 20px;
  background: #f4f6e9;
  text-align: center;
}

.transition-card p {
  margin: 0 0 6px;
  color: #004c40;
  font-size: 19px;
  font-weight: 800;
  line-height: 1.35;
}

.transition-card span {
  font-size: 17px;
}

.approach-overview {
  padding: 84px max(5vw, 32px) 92px;
  background: linear-gradient(180deg, #f5f9f1 0%, #ffffff 100%);
}

.overview-inner {
  max-width: 1260px;
  margin: auto;
}

.overview-heading {
  max-width: 850px;
  margin: 0 auto 44px;
  text-align: center;
}

.overview-heading .eyebrow {
  justify-content: center;
}

.overview-heading h2 {
  margin: 0 0 18px;
  color: #004c40;
  font-family: "Amanise";
  font-size: clamp(46px, 5vw, 68px);
  font-weight: 400;
  line-height: 1;
}

.overview-heading > p:last-child {
  margin: 0;
  color: #40534c;
  font-size: 18px;
  line-height: 1.65;
}

.overview-flow {
  width: min(100%, 940px);
  margin: auto;
  text-align: center;
}

.flow-card {
  position: relative;
  margin: auto;
  padding: 25px 34px;
  border-radius: 24px;
  box-shadow: 0 10px 30px rgba(0, 76, 64, .07);
}

.flow-kicker {
  display: block;
  margin-bottom: 8px;
  color: #6d913f;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .11em;
}

.flow-card h3 {
  margin: 0;
  color: #004c40;
  font-size: 27px;
  font-weight: 800;
  line-height: 1.08;
}

.flow-card p {
  max-width: 570px;
  margin: 10px auto 0;
  color: #50615b;
  font-size: 15.5px;
  line-height: 1.5;
}

.flow-science {
  width: 62%;
  background: #fff;
  border: 1px solid #dce9e2;
}

.flow-apd {
  width: 78%;
  padding-top: 29px;
  padding-bottom: 29px;
  background: #004c40;
}

.flow-apd .flow-kicker {
  color: #c7df92;
}

.flow-apd h3 {
  color: #fff;
  font-size: 31px;
}

.flow-pillars {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 17px;
}

.flow-pillars span {
  padding: 7px 13px;
  border-radius: 999px;
  background: rgba(255,255,255,.12);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}

.flow-tools {
  width: 88%;
  background: #fff;
  border: 1px solid #dce9e2;
}

.tool-pair {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 22px;
  align-items: center;
}

.tool-pair > span {
  color: #85bb2f;
  font-size: 30px;
  font-weight: 800;
}

.tool-pair strong,
.tool-pair small {
  display: block;
}

.tool-pair strong {
  color: #004c40;
  font-size: 19px;
}

.tool-pair small {
  margin-top: 5px;
  color: #60716a;
  font-size: 14px;
}

.flow-plate {
  width: 66%;
  background: #fff8e8;
  border: 1px solid #f1e3b7;
}

.flow-arrow {
  display: block;
  height: 38px;
  color: #85bb2f;
  font-size: 29px;
  font-weight: 800;
  line-height: 38px;
}

.flow-autonomy {
  display: flex;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
  padding: 16px 25px;
  align-items: center;
  justify-content: center;
  gap: 15px;
  border-radius: 999px;
  background: #85bb2f;
  color: #fff;
  box-shadow: 0 8px 24px rgba(77, 119, 37, .18);
}

.flow-autonomy strong {
  font-size: 17px;
}

.flow-autonomy span {
  opacity: .8;
  font-size: 19px;
}


.science-foundations {
  padding: 88px max(5vw, 32px) 94px;
  background: #fff;
}

.science-inner {
  max-width: 1260px;
  margin: auto;
}

.science-heading {
  max-width: 900px;
  margin-bottom: 42px;
}

.science-heading h2 {
  margin: 0 0 18px;
  color: #004c40;
  font-family: "Amanise";
  font-size: clamp(48px, 5vw, 68px);
  font-weight: 400;
  line-height: 1;
}

.science-lead {
  max-width: 850px;
  margin: 0;
  color: #40534c;
  font-size: 18px;
  line-height: 1.65;
}

.science-principles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  align-items: start;
}

.science-principle {
  overflow: hidden;
  border: 1px solid #e2ebe5;
  border-radius: 22px;
  background: #f9fbf8;
  transition: box-shadow .2s ease, border-color .2s ease;
}

.science-principle[open] {
  grid-column: 1 / -1;
  border-color: #cddfc4;
  box-shadow: 0 12px 32px rgba(0, 76, 64, .08);
}

.science-principle summary {
  display: grid;
  grid-template-columns: 58px 1fr;
  gap: 18px;
  padding: 27px 28px;
  align-items: start;
  cursor: pointer;
  list-style: none;
}

.science-principle summary::-webkit-details-marker {
  display: none;
}

.science-principle summary:hover {
  background: #f4f8f1;
}

.science-icon {
  display: grid;
  width: 54px;
  height: 54px;
  place-items: center;
  border-radius: 50%;
  background: #e8f2dd;
  font-size: 27px;
}

.principle-summary {
  display: block;
}

.nutrient-box {
  margin: 24px 0;
  padding: 25px 27px;
  border: 1px solid #cfe0c7;
  border-radius: 18px;
  background: #f6faf2;
}

.nutrient-box > span {
  display: block;
  margin-bottom: 10px;
  color: #4f8239;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .1em;
}

.nutrient-box > p {
  margin: 0 0 16px;
}

.nutrient-box > p:last-child {
  margin: 16px 0 0;
}

.nutrient-groups {
  display: grid;
  grid-template-columns: .7fr 1.5fr 1.5fr;
  gap: 12px;
}

.nutrient-groups > div {
  padding: 16px;
  border-radius: 13px;
  background: #fff;
}

.nutrient-groups strong,
.nutrient-groups small {
  display: block;
}

.nutrient-groups strong {
  margin-bottom: 7px;
  color: #004c40;
  font-size: 14px;
}

.nutrient-groups small {
  color: #53655e;
  font-size: 12.5px;
  line-height: 1.55;
}

.commitment-badge {
  display: inline-block;
  width: fit-content;
  margin-bottom: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #e3efd2;
  color: #004c40;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: .08em;
}

.principle-label {
  display: block;
  margin-bottom: 7px;
  color: #789d46;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .1em;
}

.principle-title {
  display: block;
  margin: 0 0 9px;
  color: #004c40;
  font-size: 21px;
  font-weight: 800;
  line-height: 1.2;
}

.principle-intro {
  display: block;
  color: #475a53;
  font-size: 15.5px;
  line-height: 1.55;
}

.principle-open {
  display: inline-flex;
  margin-top: 13px;
  align-items: center;
  gap: 7px;
  color: #5f8f34;
  font-size: 13px;
  font-weight: 800;
}

.principle-open b {
  transition: transform .2s ease;
}

.science-principle[open] .principle-open b {
  transform: rotate(180deg);
}

.science-principle[open] .principle-open {
  font-size: 0;
}

.science-principle[open] .principle-open::before {
  content: "Réduire";
  font-size: 13px;
}

.principle-detail {
  margin: 0 28px 28px 104px;
  padding-top: 24px;
  border-top: 1px solid #dce7df;
  color: #3e514a;
}

.principle-detail > p {
  max-width: 950px;
  margin: 0 0 15px;
  font-size: 16px;
  line-height: 1.65;
}

.principle-detail h4 {
  margin: 0 0 12px;
  color: #004c40;
  font-size: 18px;
  font-weight: 800;
}

.article-bridge {
  max-width: 1000px;
  margin-bottom: 26px;
  padding: 18px 22px;
  border-left: 4px solid #85bb2f;
  border-radius: 0 14px 14px 0;
  background: #f6f9f2;
}

.article-bridge > span {
  display: block;
  margin-bottom: 6px;
  color: #6e953e;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .1em;
}

.article-bridge p {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
}

.article-bridge a {
  color: #00695c;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.literature-section {
  max-width: 1050px;
  margin-top: 30px;
  padding: 28px;
  border-radius: 20px;
  background: #f0f6eb;
}

.literature-heading {
  margin-bottom: 8px;
}

.literature-heading > span {
  display: block;
  margin-bottom: 7px;
  color: #6e953e;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .1em;
}

.literature-heading h4 {
  margin-bottom: 10px;
  font-size: 22px;
}

.literature-heading p {
  margin: 0;
  font-size: 16px;
  line-height: 1.65;
}

.evidence-topic {
  padding: 23px 0 6px;
  border-top: 1px solid #d4e2cc;
}

.evidence-topic:first-of-type {
  margin-top: 20px;
}

.evidence-topic h5 {
  margin: 0 0 10px;
  color: #004c40;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.3;
}

.evidence-topic p {
  margin: 0 0 12px;
  font-size: 15.5px;
  line-height: 1.65;
}

.evidence-perspective {
  margin-top: 5px;
  padding: 22px;
  border: 0;
  border-radius: 15px;
  background: #fff;
}

.decision-box {
  max-width: 1000px;
  margin-top: 22px;
  padding: 22px 24px;
  border-radius: 16px;
  background: #004c40;
  color: #fff;
}

.decision-box > span {
  display: block;
  margin-bottom: 8px;
  color: #b9dc79;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .1em;
}

.decision-box p {
  margin: 0 0 11px;
  font-size: 15.5px;
  line-height: 1.65;
}

.decision-box p:last-child {
  margin-bottom: 0;
}

.evidence-box,
.plate-box {
  max-width: 1000px;
  margin-top: 22px;
  padding: 20px 22px;
  border-radius: 16px;
}

.evidence-box {
  background: #eef5e8;
}

.plate-box {
  background: #fff7e4;
}

.evidence-box > span,
.plate-box > span {
  display: block;
  margin-bottom: 7px;
  color: #6e953e;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .1em;
}

.evidence-box p,
.plate-box p {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
}

.detail-references {
  max-width: 1000px;
  margin-top: 26px;
}

.detail-references ol {
  margin: 0;
  padding-left: 22px;
  color: #596862;
  font-size: 13px;
  line-height: 1.55;
}

.detail-references li {
  margin-bottom: 9px;
}

.detail-references a {
  color: #39795f;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.science-conclusion {
  display: flex;
  max-width: 940px;
  margin: 38px 0 0;
  padding: 20px 26px;
  align-items: center;
  gap: 16px;
  border-radius: 18px;
  background: #004c40;
  color: #fff;
}

.science-conclusion > span {
  color: #b9dc79;
  font-size: 28px;
  font-weight: 800;
}

.science-conclusion p {
  margin: 0;
  font-size: 17px;
  line-height: 1.5;
}

.science-references {
  max-width: 940px;
  margin-top: 18px;
  border-top: 1px solid #dfe7e2;
}

.science-references summary {
  padding: 17px 0;
  color: #004c40;
  cursor: pointer;
  font-size: 14px;
  font-weight: 800;
}

.science-references ol {
  margin: 0 0 10px 20px;
  padding: 0;
  color: #5b6964;
  font-size: 13px;
  line-height: 1.55;
}

.science-references li {
  margin-bottom: 9px;
}


.science-to-plate {
  padding: 92px max(5vw, 32px) 98px;
  background: linear-gradient(180deg, #fffdf8 0%, #f6f8ef 100%);
}

.plate-tools-inner {
  max-width: 1260px;
  margin: auto;
}

.plate-tools-heading {
  max-width: 940px;
  margin-bottom: 48px;
}

.plate-tools-heading h2 {
  margin: 0 0 18px;
  color: #004c40;
  font-family: "Amanise";
  font-size: clamp(48px, 5vw, 68px);
  font-weight: 400;
  line-height: 1;
}

.plate-tools-heading > p:last-child {
  max-width: 900px;
  margin: 0;
  color: #40534c;
  font-size: 18px;
  line-height: 1.65;
}

.translation-flow {
  display: grid;
  grid-template-columns: 1fr 44px 1fr 44px 1fr;
  align-items: stretch;
}

.translation-card {
  position: relative;
  padding: 30px 28px;
  border: 1px solid #dce8d5;
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(0, 76, 64, .06);
}

.translation-number {
  position: absolute;
  top: 22px;
  right: 24px;
  color: #d8e8c8;
  font-size: 38px;
  font-weight: 900;
  line-height: 1;
}

.translation-kicker {
  display: block;
  margin-bottom: 12px;
  color: #719946;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .12em;
}

.translation-card h3 {
  max-width: 300px;
  margin: 0 0 16px;
  color: #004c40;
  font-size: 22px;
  line-height: 1.2;
}

.translation-card p {
  margin: 0 0 12px;
  color: #4b5d56;
  font-size: 15px;
  line-height: 1.6;
}

.translation-card p:last-child {
  margin-bottom: 0;
}

.translation-arrow {
  display: grid;
  place-items: center;
  color: #82aa55;
  font-size: 28px;
  font-weight: 800;
}

.scale-panel {
  margin-top: 50px;
  padding: 34px 36px;
  border-radius: 26px;
  background: #004c40;
  color: #fff;
}

.scale-heading {
  max-width: 880px;
}

.scale-heading > span,
.method-conclusion > span {
  display: block;
  margin-bottom: 8px;
  color: #b9dc79;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .12em;
}

.scale-heading h3 {
  margin: 0 0 12px;
  font-size: 27px;
  line-height: 1.2;
}

.scale-heading p {
  margin: 0;
  color: #d9e5e1;
  font-size: 16px;
  line-height: 1.6;
}

.logic-visual {
  margin-top: 32px;
  padding: 25px;
  border-radius: 20px;
  background: #fff;
  color: #004c40;
}

.logic-title {
  width: fit-content;
  margin: 0 auto 16px;
  padding: 7px 28px;
  border-radius: 999px;
  background: #e5f1dc;
  color: #004c40;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: .12em;
}

.logic-path {
  display: flex;
  margin-bottom: 22px;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #004c40;
  font-size: clamp(18px, 2vw, 27px);
  font-weight: 900;
}

.logic-path span {
  color: #55a267;
  font-size: 1.2em;
}

.logic-stages {
  display: grid;
  grid-template-columns: 1fr 30px 1fr 30px 1fr 30px 1fr;
  align-items: stretch;
}

.logic-stage {
  min-height: 285px;
  padding: 18px;
  border: 1px solid #e0eadb;
  border-radius: 18px;
  background: #f5f9f1;
  text-align: center;
}

.logic-meal {
  background: #fff8ec;
}

.logic-day {
  background: #eef7fb;
}

.logic-week {
  background: #f5f2fb;
}

.logic-stage-head {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.scale-dot {
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  border-radius: 50%;
  background: #55a85f;
  color: #fff;
  font-size: 12px;
  font-weight: 900;
}

.logic-stage-head strong {
  color: #004c40;
  font-size: 19px;
}

.logic-subtitle {
  min-height: 38px;
  margin: 12px 0 10px;
  color: #164f46;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.35;
}

.logic-stage small {
  display: block;
  margin-top: 14px;
  color: #53665f;
  font-size: 12.5px;
  line-height: 1.45;
  text-align: justify;
  hyphens: auto;
}

.logic-foods {
  display: flex;
  min-height: 76px;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 34px;
}

.logic-plate {
  display: flex;
  width: 112px;
  min-height: 76px;
  margin: 0 auto;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border: 8px solid #fff;
  border-radius: 50%;
  background: #eef3e8;
  box-shadow: 0 4px 14px rgba(0, 76, 64, .10);
  font-size: 24px;
}

.logic-day-meals {
  display: grid;
  min-height: 76px;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  align-items: stretch;
}

.logic-day-meals span {
  display: flex;
  padding: 6px 2px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: rgba(255,255,255,.75);
  font-size: 23px;
}

.logic-day-meals b {
  margin-bottom: 4px;
  color: #386b64;
  font-size: 9px;
}

.logic-calendar {
  display: grid;
  min-height: 76px;
  grid-template-columns: repeat(7, 1fr);
  gap: 3px;
  align-items: center;
}

.logic-calendar span {
  display: grid;
  height: 54px;
  place-items: start center;
  padding-top: 7px;
  border-radius: 7px;
  background: #fff;
  box-shadow: inset 0 -22px 0 #e8f1e2;
  color: #315e55;
  font-size: 9px;
  font-weight: 900;
}

.logic-arrow {
  display: grid;
  place-items: center;
  color: #55a267;
  font-size: 27px;
  font-weight: 900;
}

.logic-brackets {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: 10px;
  row-gap: 18px;
  margin-top: 18px;
}

.logic-bracket {
  position: relative;
  padding-top: 15px;
  text-align: center;
}

.logic-bracket .bracket-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 10px;
  border: 3px solid currentColor;
  border-bottom: 0;
  border-radius: 10px 10px 0 0;
}

.logic-bracket strong,
.logic-bracket small {
  display: block;
}

.logic-bracket strong {
  font-size: 14px;
  font-weight: 900;
}

.logic-bracket small {
  margin-top: 3px;
  font-size: 11.5px;
  line-height: 1.35;
}

.mealim-bracket {
  grid-column: 2 / 4;
  color: #2988a5;
}

.mmm-bracket {
  grid-column: 1 / 5;
  color: #4a9c55;
}

.method-tool p + p,
.method-conclusion p + p {
  margin-top: 12px;
}

.method-tools {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
  margin-top: 28px;
}

.method-tool {
  padding: 27px 30px;
  border: 1px solid #dce8d5;
  border-radius: 20px;
  background: #fff;
}

.method-tool-mealim {
  background: #eef5e7;
}

.method-label {
  display: block;
  margin-bottom: 8px;
  color: #719946;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .11em;
}

.method-tool h3 {
  margin: 0 0 10px;
  color: #004c40;
  font-size: 22px;
}

.method-tool p {
  margin: 0;
  color: #4b5d56;
  font-size: 15px;
  line-height: 1.6;
}

.method-conclusion {
  max-width: 1030px;
  margin: 30px auto 0;
  padding: 23px 28px;
  border-left: 4px solid #85bb2f;
  border-radius: 0 16px 16px 0;
  background: #fff;
}

.method-conclusion > span {
  color: #719946;
}

.method-conclusion p {
  margin: 0;
  color: #40534c;
  font-size: 16px;
  line-height: 1.65;
}


.translation-card p,
.scale-heading p,
.method-tool p,
.method-conclusion p,
.plate-tools-heading > p {
  text-align: justify;
  hyphens: auto;
}


.real-life {
  padding: 92px max(5vw, 32px) 100px;
  background: #fff;
}

.real-life-inner,
.autonomy-inner {
  max-width: 1260px;
  margin: auto;
}

.real-life-heading,
.autonomy-heading {
  max-width: 950px;
  margin-bottom: 44px;
}

.real-life-heading h2,
.autonomy-heading h2 {
  margin: 0 0 18px;
  color: #004c40;
  font-family: "Amanise";
  font-size: clamp(48px, 5vw, 68px);
  font-weight: 400;
  line-height: 1;
}

.real-life-heading > p:not(.eyebrow),
.autonomy-heading > p:not(.eyebrow) {
  margin: 0 0 12px;
  color: #40534c;
  font-size: 18px;
  line-height: 1.65;
  text-align: justify;
  hyphens: auto;
}

.commitment-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.commitment-card {
  position: relative;
  padding: 30px 30px 28px;
  overflow: hidden;
  border: 1px solid #dce8d5;
  border-radius: 22px;
  background: #f9fbf6;
}

.commitment-card:nth-child(2) { background: #f2f8f7; }
.commitment-card:nth-child(3) { background: #fff9ef; }
.commitment-card:nth-child(4) { background: #f5f3fa; }

.commitment-card.commitment-community {
  grid-column: 1 / -1;
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  background: #004c40;
  color: #fff;
}

.commitment-number {
  position: absolute;
  top: 22px;
  right: 26px;
  color: #dce9d4;
  font-size: 42px;
  font-weight: 900;
  line-height: 1;
}

.commitment-community .commitment-number {
  color: rgba(255,255,255,.14);
}

.commitment-kicker {
  display: block;
  margin-bottom: 10px;
  color: #719946;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .12em;
}

.commitment-community .commitment-kicker {
  color: #b9dc79;
}

.commitment-card h3 {
  max-width: 80%;
  margin: 0 0 15px;
  color: #004c40;
  font-size: 22px;
  line-height: 1.2;
}

.commitment-community h3 {
  color: #fff;
}

.commitment-card p {
  margin: 0 0 11px;
  color: #4b5d56;
  font-size: 15px;
  line-height: 1.62;
  text-align: justify;
  hyphens: auto;
}

.commitment-card p:last-child {
  margin-bottom: 0;
}

.commitment-community p {
  color: #d9e5e1;
}

.commitment-message {
  max-width: 1000px;
  margin: 32px auto 0;
  padding: 23px 28px;
  border-left: 4px solid #85bb2f;
  border-radius: 0 16px 16px 0;
  background: #f2f7ed;
}

.commitment-message > span {
  display: block;
  margin-bottom: 8px;
  color: #719946;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .12em;
}

.commitment-message p {
  margin: 0;
  color: #40534c;
  font-size: 16px;
  line-height: 1.65;
  text-align: justify;
  hyphens: auto;
}

.autonomy-section {
  padding: 92px max(5vw, 32px) 105px;
  background: linear-gradient(180deg, #f4f8ef 0%, #eaf3e2 100%);
}

.learning-path {
  display: grid;
  grid-template-columns: 1fr 28px 1fr 28px 1fr 28px 1fr 28px 1.15fr;
  align-items: stretch;
}

.learning-path article {
  padding: 22px 17px;
  border: 1px solid #d9e6d2;
  border-radius: 18px;
  background: #fff;
  text-align: center;
}

.learning-path > b {
  display: grid;
  place-items: center;
  color: #70a34f;
  font-size: 24px;
}

.learning-path article > span {
  display: grid;
  width: 32px;
  height: 32px;
  margin: 0 auto 13px;
  place-items: center;
  border-radius: 50%;
  background: #e5f1dc;
  color: #397653;
  font-size: 12px;
  font-weight: 900;
}

.learning-path strong,
.learning-path small {
  display: block;
}

.learning-path strong {
  margin-bottom: 8px;
  color: #004c40;
  font-size: 14px;
  letter-spacing: .04em;
}

.learning-path small {
  color: #5b6c65;
  font-size: 12px;
  line-height: 1.45;
}

.learning-path .learning-goal {
  border-color: #004c40;
  background: #004c40;
}

.learning-path .learning-goal > span {
  background: #b9dc79;
  color: #004c40;
}

.learning-path .learning-goal strong {
  color: #fff;
}

.learning-path .learning-goal small {
  color: #d9e5e1;
}

.autonomy-tools {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 30px;
}

.autonomy-tools p {
  margin: 0;
  padding: 21px 22px;
  border-radius: 16px;
  background: rgba(255,255,255,.7);
  color: #4b5d56;
  font-size: 14px;
  line-height: 1.55;
  text-align: justify;
  hyphens: auto;
}

.autonomy-tools strong {
  color: #004c40;
}

.autonomy-final {
  max-width: 960px;
  margin: 42px auto 0;
  text-align: center;
}

.autonomy-final > span {
  display: inline-block;
  margin-bottom: 15px;
  padding: 8px 20px;
  border-radius: 999px;
  background: #004c40;
  color: #fff;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .11em;
}

.autonomy-final p {
  margin: 0;
  color: #334b42;
  font-size: 19px;
  line-height: 1.65;
}

@media (max-width: 1100px) {
  .ambition-visual {
    width: 52%;
    margin-top: -55px;
    margin-left: 32px;
  }

  .ambition-intro h1 {
    font-size: clamp(46px, 6vw, 64px);
  }
}

@media (max-width: 991px) {
  .nutrient-groups {
    grid-template-columns: 1fr;
  }

  .real-life,
  .autonomy-section {
    padding: 70px 24px 76px;
  }

  .commitment-grid {
    grid-template-columns: 1fr;
  }

  .commitment-community {
    grid-column: auto;
    max-width: none;
  }

  .learning-path {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .learning-path > b {
    transform: rotate(90deg);
  }

  .autonomy-tools {
    grid-template-columns: 1fr;
  }


  .science-to-plate {
    padding: 70px 24px 76px;
  }

  .translation-flow {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .translation-arrow {
    transform: rotate(90deg);
  }

  .logic-path {
    flex-wrap: wrap;
    gap: 8px;
    font-size: 16px;
  }

  .logic-stages {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .logic-stage {
    min-height: 0;
  }

  .logic-arrow {
    transform: rotate(90deg);
  }

  .logic-stage {
    min-height: 0;
  }

  .logic-brackets {
    grid-template-columns: 1fr;
  }

  .mealim-bracket,
  .mmm-bracket {
    grid-column: 1;
  }

  .method-tools {
    grid-template-columns: 1fr;
  }


  .science-foundations {
    padding: 68px 24px 74px;
  }

  .science-principles {
    grid-template-columns: 1fr;
  }

  .science-principle[open] {
    grid-column: auto;
  }

  .approach-overview {
    padding: 65px 24px 72px;
  }

  .flow-science,
  .flow-apd,
  .flow-tools,
  .flow-plate {
    width: 88%;
  }

  .ambition {
    margin-top: 100px;
  }

  .section {
    padding: 54px 24px 68px;
  }

  .ambition-intro {
    max-width: none;
  }

  .ambition-visual {
    float: none;
    width: min(100%, 720px);
    margin: 10px auto 34px;
  }

  .engagement-cards {
    grid-template-columns: 1fr;
  }

  .engagements-heading {
    margin-top: 50px;
  }
}

@media (max-width: 600px) {
  .real-life,
  .autonomy-section {
    padding-left: 20px;
    padding-right: 20px;
  }

  .real-life-heading h2,
  .autonomy-heading h2 {
    font-size: 45px;
  }

  .real-life-heading > p:not(.eyebrow),
  .autonomy-heading > p:not(.eyebrow) {
    font-size: 16px;
  }

  .commitment-card {
    padding: 25px 21px;
  }

  .commitment-card h3 {
    max-width: 84%;
  }

  .commitment-message {
    padding: 21px;
  }

  .autonomy-final p {
    font-size: 17px;
  }


  .science-to-plate {
    padding-left: 20px;
    padding-right: 20px;
  }

  .plate-tools-heading h2 {
    font-size: 45px;
  }

  .plate-tools-heading > p:last-child {
    font-size: 16px;
  }

  .translation-card {
    padding: 25px 21px;
  }

  .scale-panel {
    padding: 25px 21px;
  }

  .scale-heading h3 {
    font-size: 23px;
  }

  .method-tool {
    padding: 23px 21px;
  }

  .method-conclusion {
    padding: 21px;
  }


  .science-foundations {
    padding-left: 20px;
    padding-right: 20px;
  }

  .science-heading h2 {
    font-size: 45px;
  }

  .science-lead {
    font-size: 16px;
  }

  .science-principle summary {
    grid-template-columns: 44px 1fr;
    gap: 13px;
    padding: 22px 18px;
  }

  .principle-detail {
    margin: 0 18px 22px;
    padding-top: 20px;
  }

  .literature-section {
    padding: 21px 18px;
  }

  .evidence-perspective {
    padding: 18px;
  }

  .science-icon {
    width: 42px;
    height: 42px;
    font-size: 21px;
  }

  .principle-title {
    font-size: 19px;
  }

  .principle-intro,
  .principle-detail > p {
    font-size: 15px;
  }

  .science-conclusion {
    padding: 18px 20px;
  }

  .approach-overview {
    padding-left: 20px;
    padding-right: 20px;
  }

  .overview-heading {
    text-align: left;
  }

  .overview-heading .eyebrow {
    justify-content: flex-start;
  }

  .overview-heading h2 {
    font-size: 45px;
  }

  .overview-heading > p:last-child {
    font-size: 16px;
  }

  .flow-science,
  .flow-apd,
  .flow-tools,
  .flow-plate {
    width: 100%;
  }

  .flow-card {
    padding: 22px 18px;
  }

  .tool-pair {
    grid-template-columns: 1fr;
    gap: 9px;
  }

  .tool-pair > span {
    font-size: 22px;
    line-height: 1;
  }

  .flow-autonomy {
    width: 100%;
    padding: 16px 12px;
    gap: 7px;
  }

  .flow-autonomy strong {
    font-size: 13px;
  }

  .section {
    padding-left: 20px;
    padding-right: 20px;
  }

  .ambition-intro h1 {
    font-size: 43px;
  }

  .ambition-intro h2 {
    font-size: 23px;
  }

  .ambition-copy,
  .engagement-card p {
    font-size: 16px;
  }

  .engagement-card {
    padding: 24px;
  }

  .transition-card {
    padding: 22px 20px;
  }
}

.demarche-toc{max-width:1200px;margin:0 auto;padding:32px max(5vw,24px);background:#f2f7eb;border-bottom:1px solid #dce8d5}
.demarche-toc>strong{font-size:1.5rem;color:#004c40}.demarche-toc>p{margin:8px 0 18px}
.demarche-toc>div{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
.demarche-toc a{color:#004c40;text-decoration:none;display:flex;align-items:center;gap:12px;padding:10px;border-radius:8px;background:#fff}
.demarche-toc a:hover,.demarche-toc a:focus{outline:2px solid #719946}.demarche-toc b{font-size:1.1rem;min-width:22px}
.demarche-page section[id]{scroll-margin-top:24px}
.principle-open{font-size:16px!important;font-weight:700;background:#e8f2df;border:1px solid #a8c28b;border-radius:9px;padding:10px 14px;display:inline-block}
.close-label{display:none}.science-principle[open] .open-label{display:none}.science-principle[open] .close-label{display:inline}
.engagement-card,.commitment-card{display:grid;grid-template-columns:64px minmax(0,1fr);column-gap:18px}
.engagement-topline{display:contents}.engagement-icon,.commitment-icon{grid-column:1;grid-row:1 / span 4;width:64px;height:64px;align-self:start}
.engagement-card .engagement-number,.engagement-card h3,.engagement-card p,.commitment-card .commitment-kicker,.commitment-card h3,.commitment-card p{grid-column:2}
.commitment-card h3{max-width:100%}.commitment-icon{position:static;font-size:30px;display:flex;align-items:center;justify-content:center;border-radius:16px;background:#edf4e7}
@media(max-width:650px){.demarche-toc>div{grid-template-columns:1fr}.engagement-card,.commitment-card{grid-template-columns:44px minmax(0,1fr);column-gap:12px}.engagement-icon,.commitment-icon{width:44px;height:44px;font-size:24px}}

.demarche-page{padding-top:calc(var(--demarche-header-height,153px) + 16px)}
.demarche-page .ambition{margin-top:0}
.demarche-return{max-width:1200px;margin:0 auto;padding:0 max(5vw,24px) 18px}
.demarche-return a{display:inline-flex;align-items:center;padding:11px 18px;border:2px solid #004c40;border-radius:10px;background:#fff;color:#004c40;font-size:15px;font-weight:700;text-decoration:none}
.demarche-return a:hover{background:#edf4e7}.demarche-return a:focus-visible{outline:3px solid #719946;outline-offset:3px}
.demarche-page section[id]{scroll-margin-top:calc(var(--demarche-header-height,153px) + 24px)}
</style>
